import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSpinner } from "react-icons/fa";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

// Components
import { FloatingActionButtons } from "./ui/FloatingActionButtons";
import Header from "./ui/Header";
import Logo from "./ui/Logo";
import Sidebar from "./ui/Sidebar";
import SidebarNav from "./ui/SidebarNav";

// API and types
import { allNav, NavItem } from "@/config/navigation";
import { Role } from "@/constants/enums";
import { EventBanner, EventNotificationModal } from "@/features/events";
import { useEventNotifications } from "@/hooks/useEventNotifications";
import { NariShaktiLogo } from "../assets";
import { ROUTES } from "../constants/routes";
import { useAppContext } from "../contexts/AppContext";

// Helper function to find labelKey by path in navigation structure
function findLabelKeyByPath(
  navItems: NavItem[],
  pathname: string
): string | undefined {
  for (const item of navItems) {
    if (item.to === pathname) return item.labelKey;
    if (item.children) {
      const childKey = findLabelKeyByPath(item.children, pathname);
      if (childKey) return childKey;
    }
  }
  return undefined;
}

// Memoized Header component with performance optimization
const MemoizedHeader = memo(
  ({
    onMobileMenuToggle,
    activeBannerEvents,
    openModal,
  }: {
    onMobileMenuToggle: () => void;
    activeBannerEvents: any[];
    openModal: (event: any) => void;
  }) => {
    const { user } = useAppContext();
    const { t } = useTranslation();
    const location = useLocation();

    // Get page title and description from current route with localization
    const getPageInfo = useCallback(
      (pathname: string) => {
        // Handle root and dashboard
        if (
          !pathname ||
          pathname === ROUTES.ROOT ||
          pathname === ROUTES.DASHBOARD
        ) {
          return {
            title: t("navigation.dashboard"),
            description: t(
              "navigation.dashboardDescription",
              "Welcome to your dashboard"
            ),
          };
        }

        // First try to find exact match in navigation structure
        const labelKey = findLabelKeyByPath(allNav, pathname);
        if (labelKey) {
          const titleKey = `navigation.${labelKey}`;
          const descKey = `navigation.${labelKey}Description`;
          return {
            title: t(titleKey, labelKey),
            description: t(descKey, ""),
          };
        }

        // Handle dynamic commission user details route
        if (pathname.startsWith("/admin/commissions/user/")) {
          return {
            title: t("navigation.commissionDetails", "Savings Details"),
            description: t(
              "navigation.commissionDetailsDescription",
              "Review and manage a member's savings records"
            ),
          };
        }

        // Fallback: use path segments for common routes
        const pathSegments = pathname.split("/").filter(Boolean);
        const lastSegment = pathSegments[pathSegments.length - 1].replace(
          /-/g,
          ""
        );
        const titleKey = `navigation.${lastSegment}`;
        const descKey = `navigation.${lastSegment}Description`;

        return {
          title: t(titleKey, lastSegment),
          description: t(descKey, ""),
        };
      },
      [t]
    );

    const pageInfo = getPageInfo(location.pathname);

    return (
      <Header
        title={pageInfo.title}
        description={pageInfo.description}
        onMobileMenuToggle={onMobileMenuToggle}
        user={
          user
            ? {
                name: user.fullName || t("common.actions.add"),
                email: user.contact?.email || "",
                avatarUrl: NariShaktiLogo,
              }
            : undefined
        }
        activeEvent={
          activeBannerEvents.length > 0 ? activeBannerEvents[0] : undefined
        }
        onEventClick={() =>
          activeBannerEvents.length > 0 && openModal(activeBannerEvents[0])
        }
      />
    );
  }
);
MemoizedHeader.displayName = "MemoizedHeader";

// Memoized Sidebar content component with role-based filtering
const MemoizedSidebarContent = memo(() => {
  const { user } = useAppContext();

  // Enhanced navigation filtering with role-based access and single child optimization
  const getFilteredNav = useCallback(
    (navItems: NavItem[], userRole?: Role): NavItem[] => {
      if (!userRole) return [];

      return navItems
        .map((item) => {
          // Check if current item should be shown based on role
          const hasAccess = !item.roles || item.roles.includes(userRole);

          // If item has children, check if all original children are admin-only
          if (item.children && item.children.length > 0) {
            const allChildrenAdminOnly = item.children.every(
              (child) =>
                child.roles &&
                child.roles.includes(Role.ADMIN) &&
                !child.roles.includes(userRole)
            );

            // If all children are admin-only and user is not admin, exclude the entire parent
            if (allChildrenAdminOnly && userRole !== Role.ADMIN) {
              return null;
            }

            // Filter children recursively
            const filteredChildren = getFilteredNav(item.children, userRole);

            // If no children remain after filtering and parent has no direct route, exclude it
            if (filteredChildren.length === 0 && !item.to) {
              return null;
            }

            // If only one child and parent has no direct route, show child as direct nav
            if (filteredChildren.length === 1 && !item.to) {
              return {
                ...filteredChildren[0],
                roles: filteredChildren[0].roles || item.roles,
              };
            }

            // Return parent with filtered children
            return {
              ...item,
              children: filteredChildren,
            };
          }

          // If item has no access, exclude it
          if (!hasAccess) {
            return null;
          }

          // Return item if it has direct access (no children or empty children)
          return { ...item };
        })
        .filter((item): item is NavItem => item !== null);
    },
    []
  );

  const filteredNav = getFilteredNav(allNav, user?.role);

  return (
    <>
      <Logo />
      <div className="flex-1 overflow-y-auto scroll-smooth">
        <SidebarNav nav={filteredNav} />
      </div>
      <div className="mt-auto pt-4">
        {/* <LanguageSwitcher /> */}
      </div>
    </>
  );
});
MemoizedSidebarContent.displayName = "MemoizedSidebarContent";

// Main Layout component with optimized performance
const Layout: React.FC = () => {
  const { loading, error } = useAppContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Event notifications management
  const {
    activeBannerEvents,
    activeModalEvent,
    isModalOpen,
    dismissBanner,
    openModal,
    closeModal,
  } = useEventNotifications();

  // Handle authentication errors
  useEffect(() => {
    if (error && error.includes("unauthorized")) {
      navigate(ROUTES.HOME);
    }
  }, [error, navigate]);

  // Close mobile menu when route changes
  const location = useLocation();
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Toggle mobile menu
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  // Close mobile menu
  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
        <div className="text-center">
          <FaSpinner className="animate-spin h-8 w-8 text-primary-600 mx-auto mb-4" />
          <p className="text-[var(--text-secondary)] font-medium">
            {t("common.loadingState.pleaseWait", "Loading...")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {/* Main Application Layout */}
      <div className="flex h-screen overflow-hidden">
        {/* Desktop Sidebar */}
        <div className="hidden lg:flex lg:flex-shrink-0">
          <div className="flex flex-col w-64">
            <Sidebar>
              <MemoizedSidebarContent />
            </Sidebar>
          </div>
        </div>

        {/* Mobile Sidebar Overlay */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={closeMobileMenu}
            />

            {/* Mobile Sidebar */}
            <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
              <div className="flex flex-col h-full">
                {/* Mobile sidebar header with close button */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <Logo />
                  <button
                    onClick={closeMobileMenu}
                    className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                    aria-label={t("navigation.closeMenu", "Close menu")}
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Mobile navigation */}
                <div className="flex-1 overflow-y-auto scroll-smooth p-4">
                  <SidebarNav
                    nav={(() => {
                      const { user } = useAppContext();
                      const getFilteredNav = (
                        navItems: NavItem[],
                        userRole?: Role
                      ): NavItem[] => {
                        if (!userRole) return [];
                        return navItems
                          .map((item) => {
                            const hasAccess =
                              !item.roles || item.roles.includes(userRole);
                            if (item.children && item.children.length > 0) {
                              const allChildrenAdminOnly = item.children.every(
                                (child) =>
                                  child.roles &&
                                  child.roles.includes(Role.ADMIN) &&
                                  !child.roles.includes(userRole)
                              );
                              if (
                                allChildrenAdminOnly &&
                                userRole !== Role.ADMIN
                              ) {
                                return null;
                              }
                              const filteredChildren = getFilteredNav(
                                item.children,
                                userRole
                              );
                              if (filteredChildren.length === 0 && !item.to) {
                                return null;
                              }
                              if (filteredChildren.length === 1 && !item.to) {
                                return {
                                  ...filteredChildren[0],
                                  roles:
                                    filteredChildren[0].roles || item.roles,
                                };
                              }
                              return {
                                ...item,
                                children: filteredChildren,
                              };
                            }
                            if (!hasAccess) {
                              return null;
                            }
                            return { ...item };
                          })
                          .filter((item): item is NavItem => item !== null);
                      };
                      return getFilteredNav(allNav, user?.role);
                    })()}
                  />
                </div>

                {/* Mobile language switcher */}
                <div className="mt-auto p-4 border-t border-gray-200">
                  {/* <LanguageSwitcher /> */}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <MemoizedHeader
            onMobileMenuToggle={toggleMobileMenu}
            activeBannerEvents={activeBannerEvents}
            openModal={openModal}
          />

          {/* Page Content */}
          <main className="flex-1 relative overflow-y-auto focus:outline-none">
            <div className="py-8">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Event Banners */}
                {activeBannerEvents.map((event) => (
                  <EventBanner
                    key={event.id}
                    event={event}
                    onDismiss={() => dismissBanner(event.id)}
                    onViewDetails={() => openModal(event)}
                  />
                ))}

                <Outlet />
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Floating Action Buttons */}
      <FloatingActionButtons />

      {/* Event Notification Modal */}
      {activeModalEvent && (
        <EventNotificationModal
          event={activeModalEvent}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default Layout;
